import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { ListItemText, IconButton, ListItemSecondaryAction } from "@mui/material";
import { ArrowUpward, ArrowDownward, DragIndicator } from "@mui/icons-material";

import { CheckboxInput } from "@framework/mui-inputs-core";

import { StyledList, StyledListItem } from "../styled";

const DEFAULT_UNKNOWN_TAB_MSG = "pages.dashboard.unknownMainModule";

export interface IDraggableTabListProps<T extends object> {
  initialOrder: string[];
  initialCheckedState: T;
  tabLabelMessageIdById: Record<string, string>;
  unknownTabLabelMessageId?: string;
  tabCheckboxNameById?: Record<string, keyof T>;
  useTabIdAsCheckboxName?: boolean;
  maxCheckedCount?: number;
  onOrderChange: (newOrder: string[]) => void;
  onCheckedChange: (newState: T) => void;
  onValidationFailed: () => void;
}

export function DraggableTabList<T extends object>({
  initialOrder,
  initialCheckedState,
  tabLabelMessageIdById,
  unknownTabLabelMessageId = DEFAULT_UNKNOWN_TAB_MSG,
  tabCheckboxNameById,
  useTabIdAsCheckboxName = false,
  maxCheckedCount,
  onOrderChange,
  onCheckedChange,
  onValidationFailed,
}: IDraggableTabListProps<T>): React.ReactElement {
  const [tabsOrder, setTabsOrder] = useState<string[]>(initialOrder);
  const [checkedState, setCheckedState] = useState<T>(initialCheckedState);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  useEffect(() => {
    setTabsOrder(initialOrder);
    setCheckedState(initialCheckedState);
  }, [initialOrder, initialCheckedState]);

  const moveTab = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= tabsOrder.length) return;

      const newOrder = [...tabsOrder];
      const [movedTab] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedTab);
      setTabsOrder(newOrder);
      onOrderChange(newOrder);
    },
    [tabsOrder, onOrderChange],
  );

  const handleMouseDown = useCallback((index: number) => {
    setDraggingIndex(index);
  }, []);

  const handleMouseMove = useCallback(
    (_event: React.MouseEvent, index: number) => {
      if (draggingIndex === null || draggingIndex === index) return;
      moveTab(draggingIndex, index);
      setDraggingIndex(index);
    },
    [draggingIndex, moveTab],
  );

  const handleMouseUp = useCallback(() => {
    setDraggingIndex(null);
  }, []);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;

      if (checked && typeof maxCheckedCount === "number") {
        const selectedCount = Object.values(checkedState as Record<string, unknown>).filter(
          value => value === true,
        ).length;
        if (selectedCount >= maxCheckedCount) {
          return;
        }
      }

      const newState = {
        ...checkedState,
        [name]: checked,
      } as T;

      const hasSelectedCheckbox = Object.values(newState as Record<string, unknown>).some(value => value === true);
      if (!hasSelectedCheckbox) {
        onValidationFailed();
        return;
      }

      setCheckedState(newState);
      onCheckedChange(newState);
    },
    [checkedState, maxCheckedCount, onCheckedChange, onValidationFailed],
  );

  const selectedCheckboxCount = Object.values(checkedState as Record<string, unknown>).filter(
    value => value === true,
  ).length;
  const isSelectionLimitReached = typeof maxCheckedCount === "number" && selectedCheckboxCount >= maxCheckedCount;

  const checkboxBinding = (tab: string): { name: string; checked: boolean } => {
    if (useTabIdAsCheckboxName) {
      const rec = checkedState as Record<string, boolean>;
      return { name: tab, checked: Boolean(rec[tab]) };
    }

    const key = tabCheckboxNameById?.[tab];
    const nameStr = key !== undefined ? String(key) : tab;
    const checked = key !== undefined ? Boolean(checkedState[key]) : false;

    return { name: nameStr, checked };
  };

  return (
    <StyledList onMouseUp={handleMouseUp}>
      {tabsOrder.map((tab, index) => {
        const { name: checkboxName, checked } = checkboxBinding(tab);

        return (
          <StyledListItem
            key={tab}
            onMouseDown={() => handleMouseDown(index)}
            onMouseMove={event => handleMouseMove(event, index)}
            className={draggingIndex === index ? "dragging" : ""}
          >
            <CheckboxInput
              name={checkboxName}
              label=""
              checked={checked}
              disabled={!checked && isSelectionLimitReached}
              onChange={handleCheckboxChange}
            />
            <IconButton>
              <DragIndicator />
            </IconButton>
            <ListItemText
              primary={
                tabLabelMessageIdById[tab] ? (
                  <FormattedMessage id={tabLabelMessageIdById[tab]} />
                ) : (
                  <FormattedMessage id={unknownTabLabelMessageId} values={{ id: tab }} />
                )
              }
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="move up"
                onClick={() => moveTab(index, index - 1)}
                disabled={index === 0}
              >
                <ArrowUpward />
              </IconButton>
              <IconButton
                edge="end"
                aria-label="move down"
                onClick={() => moveTab(index, index + 1)}
                disabled={index === tabsOrder.length - 1}
              >
                <ArrowDownward />
              </IconButton>
            </ListItemSecondaryAction>
          </StyledListItem>
        );
      })}
    </StyledList>
  );
}
